import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, Calendar as CalIcon, ChevronRight, FileText, ArrowLeft, ShieldCheck } from "lucide-react";
import FileUpload from "@/components/FileUpload";

const DEPT_LABEL = {
  GOV: "Governance & Administration", HTH: "Health Services", EDU: "Education",
  HSG: "Housing & Public Works", CAP: "Capital Projects", LND: "Lands & Resources",
  SOC: "Social Services", ECO: "Economic Development", CUL: "Culture & Language",
};

const STATUS_LABEL = {
  open: "Accepting applications",
  in_selection: "Pending selection results",
  filled: "Filled",
  withdrawn: "Withdrawn",
};

export default function Careers() {
  const { id } = useParams();
  const nav = useNavigate();
  const [postings, setPostings] = useState([]);

  useEffect(() => {
    api.get("/hrm/postings").then(r => setPostings(r.data)).catch(() => {});
  }, []);

  if (id) return <PostingDetail id={id} onBack={() => nav("/careers")}/>;

  const open = postings.filter(p => p.status === "open");
  const pending = postings.filter(p => p.status === "in_selection");
  const filled = postings.filter(p => p.status === "filled").slice(0, 4);

  return (
    <div className="max-w-[1240px] mx-auto px-6 py-16">
      <div className="label-mono">Careers</div>
      <h1 className="font-display text-5xl mt-3 leading-tight">
        Work with <span className="text-white/70">Kasabonika Lake.</span>
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl leading-relaxed">
        Positions with the Nation and its programs. Applications are reviewed by HR against the
        posted qualifications. Band members are given hiring preference where noted.
      </p>

      <section className="mt-14">
        <div className="label-mono mb-6">Open positions ({open.length})</div>
        {open.length === 0 ? (
          <EmptyBlock label="No positions are currently accepting applications."/>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="careers-open-list">
            {open.map(p => <PostingCard key={p.id} p={p}/>)}
          </div>
        )}
      </section>

      {pending.length > 0 && (
        <section className="mt-14">
          <div className="label-mono mb-6">Pending selection results</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pending.map(p => <PostingCard key={p.id} p={p} muted/>)}
          </div>
        </section>
      )}

      {filled.length > 0 && (
        <section className="mt-14">
          <div className="label-mono mb-6">Recently filled</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filled.map(p => (
              <div key={p.id} className="border border-white/8 bg-white/[0.015] p-4 opacity-70">
                <div className="tag" style={{color: "#89b4fa"}}>Filled</div>
                <div className="font-display text-base mt-2">{p.title}</div>
                <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mt-1">{DEPT_LABEL[p.department]}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PostingCard({ p, muted }) {
  const st = STATUS_LABEL[p.status];
  return (
    <Link
      to={`/careers/${p.id}`}
      className={`block border border-white/8 bg-white/[0.015] p-6 hover:border-[color:var(--cobalt)]/60 ${muted ? "opacity-70" : ""}`}
      data-testid={`careers-post-${p.id}`}
    >
      <div className="flex items-center justify-between">
        <span className="tag" style={{color: p.status === "open" ? "#89b4fa" : "#d4b876"}}>{st}</span>
        <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">{DEPT_LABEL[p.department] || p.department}</span>
      </div>
      <div className="font-display text-2xl mt-3 leading-tight">{p.title}</div>
      <div className="text-white/60 text-sm mt-3 leading-relaxed line-clamp-2">{p.summary}</div>
      <div className="mt-5 pt-4 border-t border-white/8 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-white/50">
        <span className="flex items-center gap-3">
          {p.hours_per_week && <span className="flex items-center gap-1"><Clock size={11}/>{p.hours_per_week}h/wk</span>}
          {p.location && <span className="flex items-center gap-1"><MapPin size={11}/>{p.location.split(",")[0]}</span>}
        </span>
        <span>closes {p.closes_at ? new Date(p.closes_at).toLocaleDateString() : "—"}</span>
      </div>
    </Link>
  );
}

function EmptyBlock({ label }) {
  return (
    <div className="border border-white/8 bg-white/[0.015] p-10 text-center">
      <div className="text-white/50">{label}</div>
    </div>
  );
}

function PostingDetail({ id, onBack }) {
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  useEffect(() => { api.get(`/hrm/postings/${id}`).then(r => setPost(r.data)).catch(()=>{}); }, [id]);
  if (!post) return <div className="max-w-[1240px] mx-auto px-6 py-16 font-mono text-xs text-white/40">Loading posting…</div>;

  const canApply = post.status === "open";
  const closesDate = post.closes_at ? new Date(post.closes_at) : null;
  const daysToClose = closesDate ? Math.ceil((closesDate - new Date()) / 86400000) : null;

  if (submitted) return (
    <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
      <div className="w-14 h-14 mx-auto border border-[color:var(--cobalt)]/60 flex items-center justify-center text-[color:var(--cobalt)]">
        <ShieldCheck size={28}/>
      </div>
      <div className="label-mono mt-6">Application received</div>
      <h1 className="font-display text-4xl mt-3 leading-tight">Pending selection results.</h1>
      <p className="text-white/60 mt-6 leading-relaxed">
        Your application for <span className="text-white/90">{post.title}</span> was received. HR will review all applications after the deadline
        ({closesDate ? closesDate.toLocaleDateString() : "—"}) and issue a Notice of Selection to the chosen candidate. All other applicants will be notified via the portal.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        {user && (
          <Link to="/portal/careers/mine" className="btn-cta primary text-xs">
            <FileText size={12}/> My applications
          </Link>
        )}
        <button onClick={onBack} className="btn-cta text-xs">
          <ArrowLeft size={12}/> Back to careers
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-16">
      <button onClick={onBack} className="btn-cta text-xs mb-6" data-testid="careers-back">
        <ArrowLeft size={12}/> All careers
      </button>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
        <div>
          <div className="label-mono">{DEPT_LABEL[post.department] || post.department}</div>
          <h1 className="font-display text-4xl mt-3 leading-tight">{post.title}</h1>
          <div className="flex flex-wrap gap-4 mt-4 font-mono text-[10px] text-white/50 uppercase tracking-widest">
            <span className="flex items-center gap-1"><Briefcase size={11}/> {post.position_type?.replace("_"," ")}</span>
            {post.hours_per_week && <span className="flex items-center gap-1"><Clock size={11}/> {post.hours_per_week}h/wk</span>}
            {post.salary_range && <span>{post.salary_range}</span>}
            {post.location && <span className="flex items-center gap-1"><MapPin size={11}/> {post.location}</span>}
          </div>

          <div className="text-white/80 mt-8 leading-relaxed">{post.summary}</div>

          {post.description && (
            <>
              <h2 className="font-display text-2xl mt-10">About the role</h2>
              <div className="mt-3 text-white/70 whitespace-pre-wrap leading-relaxed">{post.description}</div>
            </>
          )}
          {post.responsibilities && (
            <>
              <h2 className="font-display text-2xl mt-10">Responsibilities</h2>
              <div className="mt-3 text-white/70 whitespace-pre-wrap leading-relaxed">{post.responsibilities}</div>
            </>
          )}
          {post.qualifications && (
            <>
              <h2 className="font-display text-2xl mt-10">Qualifications</h2>
              <div className="mt-3 text-white/70 whitespace-pre-wrap leading-relaxed">{post.qualifications}</div>
            </>
          )}
        </div>
        <aside>
          <div className="border border-white/12 bg-white/[0.02] p-6 sticky top-24">
            <div className="tag" style={{color: canApply ? "#89b4fa" : "#d4b876"}}>{STATUS_LABEL[post.status]}</div>
            <div className="mt-4">
              <div className="label-mono">Deadline</div>
              <div className="font-display text-2xl mt-1">{closesDate ? closesDate.toLocaleDateString() : "—"}</div>
              {daysToClose !== null && daysToClose > 0 && (
                <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
                  {daysToClose} {daysToClose === 1 ? "day" : "days"} left
                </div>
              )}
            </div>
            <div className="mt-6 space-y-1 text-sm">
              {post.require_resume && <div className="text-white/70">• Résumé required</div>}
              {post.require_cover_letter && <div className="text-white/70">• Cover letter required</div>}
              {post.preferred_band_member && <div className="text-[color:var(--cobalt)]">• KLFN band members preferred</div>}
            </div>

            {canApply ? (
              <button onClick={()=>setShowForm(true)} className="btn-cta primary w-full mt-6 justify-center" data-testid="careers-apply-btn">
                Apply for this position
              </button>
            ) : (
              <div className="mt-6 font-mono text-[10px] text-white/50 uppercase tracking-widest text-center">
                Not currently accepting applications.
              </div>
            )}
          </div>
        </aside>
      </div>

      {showForm && (
        <ApplicationForm post={post} onClose={()=>setShowForm(false)} onSubmitted={(a)=>{setShowForm(false); setSubmitted(a);}}/>
      )}
    </div>
  );
}

function ApplicationForm({ post, onClose, onSubmitted }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    applicant_name: user ? `${user.first_name} ${user.last_name}` : "",
    applicant_email: user?.email || "",
    applicant_phone: user?.phone || "",
    is_band_member: !!user?.band_registry_number,
    resume_file: null,   // { id, original_filename, size }
    cover_letter: "",
    cover_letter_file: null,
    answers: (post.questions || []).map(q => ({ id: q.id, prompt: q.prompt, value: "" })),
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        applicant_name: form.applicant_name,
        applicant_email: form.applicant_email,
        applicant_phone: form.applicant_phone,
        is_band_member: form.is_band_member,
        resume_url: form.resume_file ? `/api/hrm/applications/files/${form.resume_file.id}` : "",
        cover_letter: form.cover_letter,
        cover_letter_url: form.cover_letter_file ? `/api/hrm/applications/files/${form.cover_letter_file.id}` : "",
        answers: form.answers,
      };
      const r = await api.post(`/hrm/postings/${post.id}/apply`, payload);
      onSubmitted(r.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit}
        className="max-w-2xl mx-auto frost-glass border border-white/12 p-6 my-8 space-y-4" data-testid="careers-apply-modal">
        <div>
          <div className="label-mono">Application form</div>
          <h2 className="font-display text-2xl mt-1">Apply — {post.title}</h2>
          <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
            Deadline {new Date(post.closes_at).toLocaleDateString()}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <div className="label-mono mb-1">Full name <span className="text-[color:var(--signal)]">*</span></div>
            <input required value={form.applicant_name} onChange={e=>setForm({...form, applicant_name: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="apply-name"/>
          </label>
          <label className="block">
            <div className="label-mono mb-1">Email <span className="text-[color:var(--signal)]">*</span></div>
            <input required type="email" value={form.applicant_email} onChange={e=>setForm({...form, applicant_email: e.target.value})} className="w-full px-3 py-2 text-sm" data-testid="apply-email"/>
          </label>
          <label className="block">
            <div className="label-mono mb-1">Phone</div>
            <input value={form.applicant_phone} onChange={e=>setForm({...form, applicant_phone: e.target.value})} className="w-full px-3 py-2 text-sm"/>
          </label>
          <label className="col-span-2 flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={form.is_band_member} onChange={e=>setForm({...form, is_band_member: e.target.checked})}/>
            I am a Kasabonika Lake First Nation band member
          </label>
        </div>

        {post.require_resume && (
          <div>
            <div className="label-mono mb-2">Résumé <span className="text-[color:var(--signal)]">*</span></div>
            <FileUpload
              endpoint={`/hrm/postings/${post.id}/upload`}
              accept=".pdf,.doc,.docx,.rtf,.txt"
              maxSizeMb={10}
              label="Attach résumé (PDF, DOCX, RTF)"
              value={form.resume_file}
              onChange={({ file }) => setForm({...form, resume_file: file})}
              testid="apply-resume-upload"
            />
          </div>
        )}
        {post.require_cover_letter && (
          <div>
            <div className="label-mono mb-2">Cover letter <span className="text-[color:var(--signal)]">*</span></div>
            <textarea rows={5} value={form.cover_letter} onChange={e=>setForm({...form, cover_letter: e.target.value})}
              placeholder="Write your cover letter here — 2–4 short paragraphs is plenty."
              className="w-full px-3 py-2 text-sm" data-testid="apply-cover"/>
            <div className="mt-3">
              <div className="label-mono mb-2">— or attach a file instead —</div>
              <FileUpload
                endpoint={`/hrm/postings/${post.id}/upload`}
                accept=".pdf,.doc,.docx,.rtf,.txt"
                maxSizeMb={5}
                label="Attach cover letter (PDF, DOCX)"
                value={form.cover_letter_file}
                onChange={({ file }) => setForm({...form, cover_letter_file: file})}
                testid="apply-cover-upload"
              />
            </div>
          </div>
        )}
        {(post.questions || []).map((q, i) => (
          <label key={q.id} className="block">
            <div className="label-mono mb-1">{q.prompt}{q.required && <span className="text-[color:var(--signal)]"> *</span>}</div>
            {q.kind === "long_text" ? (
              <textarea required={q.required} rows={3} value={form.answers[i].value}
                onChange={e=>{const a=[...form.answers];a[i].value=e.target.value;setForm({...form, answers: a});}}
                className="w-full px-3 py-2 text-sm"/>
            ) : (
              <input required={q.required} value={form.answers[i].value}
                onChange={e=>{const a=[...form.answers];a[i].value=e.target.value;setForm({...form, answers: a});}}
                className="w-full px-3 py-2 text-sm"/>
            )}
          </label>
        ))}

        <div className="flex justify-end gap-2 pt-4 border-t border-white/8">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={busy || (post.require_resume && !form.resume_file) || (post.require_cover_letter && !form.cover_letter && !form.cover_letter_file)} className="btn-cta primary" data-testid="apply-submit">
            {busy ? "Submitting…" : "Submit application"}
          </button>
        </div>
      </form>
    </div>
  );
}
